import { Link } from 'react-router-dom';
import {
  AlignLeft,
  Bookmark,
  CalendarCheck,
  Gauge,
  Hash,
  ListTodo,
  Quote,
  Search,
  Target,
  Type,
} from 'lucide-react';
import { usePageMeta } from '../usePageMeta';
import { MindFlowExtract } from '../components/MindFlowExtract';
import { SectionHeading } from '../components/SectionHeading';
import { CtaBanner } from '../components/CtaBanner';

/**
 * The seven outputs, grouped by what they're actually for — naming the
 * entry, reading it, and keeping the parts that outlive it.
 */
const GROUPS = [
  {
    kicker: 'described',
    title: 'It names the entry',
    items: [
      {
        icon: Type,
        tone: 'ink',
        label: 'Title',
        body: 'A real title rather than a date stamp, so the entry is findable a year later.',
      },
      {
        icon: AlignLeft,
        tone: 'g-blue',
        label: 'Summary',
        body: 'The gist in a sentence. Months of writing become something you can skim.',
      },
    ],
  },
  {
    kicker: 'classified',
    title: 'It reads mood and theme',
    items: [
      {
        icon: Gauge,
        tone: 'g-ember',
        label: 'Sentiment',
        body: 'How the day actually felt, tracked over time without you rating anything.',
      },
      {
        icon: Hash,
        tone: 'g-pink',
        label: 'Tags',
        body: 'Themes recognized from the writing itself — no taxonomy for you to maintain.',
      },
    ],
  },
  {
    kicker: 'kept',
    title: 'It keeps what outlives the entry',
    items: [
      {
        icon: Target,
        tone: 'g-sun',
        label: 'Goals',
        body: 'Mention that you want to run three mornings a week and it becomes a goal, tracked from then on.',
      },
      {
        icon: ListTodo,
        tone: 'g-blue',
        label: 'Tasks',
        body: 'A sentence with a deadline inside it becomes a task with a due date.',
      },
      {
        icon: Bookmark,
        tone: 'g-violet',
        label: 'Memories',
        body: "Details worth holding onto — a friend's move, a name, a decision — preserved and searchable.",
      },
    ],
  },
] as const;

const COMPARE_ROWS = [
  ['Write, then tag and title it yourself.', 'Write. The title, summary, and tags are already there.'],
  ['Tasks live in another app you forget to open.', 'Tasks fall out of the sentence that created them.'],
  ['Goals are a list you review each January.', 'Goals are recognized as you mention them, then tracked.'],
  ['Last year is a wall of text to scroll.', 'Ask a question; get the entry that answers it.'],
] as const;

export function MindFlow(): React.JSX.Element {
  usePageMeta(
    'MindFlow AI — journaling that organizes itself, by Inafana',
    'MindFlow is a journaling app where the AI does the organizing. Write one free-form entry; Gemini turns it into a title, summary, sentiment, tags, goals, tasks, and memories, then indexes it so you can ask questions about your own life.',
  );

  return (
    <>
      <section className="product-hero">
        <div className="blob blob-ember" style={{ width: 560, height: 560, top: -160, left: '-6%' }} />
        <div className="blob blob-pink" style={{ width: 440, height: 440, bottom: -120, right: '-4%' }} />
        <div className="container">
          <div className="product-hero-grid">
            <div className="product-hero-copy">
              <img
                className="product-mark"
                src="/assets/products/mindflow.svg"
                alt="MindFlow AI"
              />
              <h1 className="h1 reveal" data-delay="1">
                You just write.
                <br />
                <span className="grad">
                  The structure
                  <br />
                  falls out.
                </span>
              </h1>
              <p className="lede reveal" data-delay="2">
                MindFlow is a journaling app where you never build a to-do list, name an
                entry, or pick a tag. Write one free-form entry — Gemini reads it back as
                a title, summary, sentiment, tags, goals, tasks, and memories, then
                indexes it so you can ask questions about your own life.
              </p>
              <div className="hero-ctas reveal" data-delay="3">
                <Link to="/#contact" className="btn btn-primary btn-lg">
                  Talk to us about MindFlow <span className="arrow">→</span>
                </Link>
                <Link to="/mindflow#extract" className="btn btn-ghost btn-lg">
                  See what one entry produces
                </Link>
              </div>
            </div>
            <div className="reveal" data-delay="2">
              <MindFlowExtract />
            </div>
          </div>
        </div>
      </section>

      <section id="extract" className="section-surface">
        <div className="container">
          <SectionHeading
            anim="wipe"
            title={
              <>
                One entry in. <span className="si accent-text">Seven things out</span>.
              </>
            }
            lede="Every one of these is produced from the writing itself. None of them is a field you filled in."
          />
          <div className="mf-groups">
            {GROUPS.map((g, gi) => (
              <article key={g.kicker} className="mf-group reveal" data-delay={gi + 1}>
                <div className="mf-group-kicker">{g.kicker}</div>
                <h3 className="h3">{g.title}</h3>
                <ul>
                  {g.items.map((item) => (
                    <li key={item.label}>
                      <span className={`mf-ic ${item.tone}`} aria-hidden="true">
                        <item.icon size={15} strokeWidth={2} />
                      </span>
                      <div>
                        <b>{item.label}</b>
                        <span>{item.body}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="container">
          <div className="feature-split">
            <div className="reveal">
              <h2 className="h2">
                You never make <span className="si accent-text">a list</span>.
              </h2>
              <p className="lede">
                Every other journaling app asks you to do the organizing after you've
                written — tag it, title it, move the action item somewhere else. That
                second step is the one people quietly stop doing. MindFlow removes it
                rather than reminding you about it.
              </p>
            </div>
            <div className="compare reveal" data-delay="1">
              <div className="compare-row compare-head">
                <div className="compare-cell old">Journaling apps</div>
                <div className="compare-cell new">MindFlow</div>
              </div>
              {COMPARE_ROWS.map(([old, next]) => (
                <div key={next} className="compare-row">
                  <div className="compare-cell old">{old}</div>
                  <div className="compare-cell new">{next}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-surface">
        <div className="container">
          <div className="feature-split">
            <div className="reveal">
              <h2 className="h2">
                Ask your own life <span className="si accent-text">a question</span>.
              </h2>
              <p className="lede">
                Entries are indexed as they're written. So instead of scrolling back
                through months looking for the day something happened, you ask — and the
                answer arrives with the entry it came from.
              </p>
              <ul className="feature-list">
                <li>
                  <Search size={20} strokeWidth={1.8} />
                  <div>
                    <b>Ask in plain language</b>
                    <span>
                      "When did Sara say she was moving?" works. No keywords, and no
                      remembering what you called the entry.
                    </span>
                  </div>
                </li>
                <li>
                  <Quote size={20} strokeWidth={1.8} />
                  <div>
                    <b>Answered from your entries</b>
                    <span>
                      Every answer names the entry it came from, so you can open it and
                      check rather than take it on trust.
                    </span>
                  </div>
                </li>
                <li>
                  <CalendarCheck size={20} strokeWidth={1.8} />
                  <div>
                    <b>Across the whole archive</b>
                    <span>
                      One question searches everything you've ever written, not just the
                      last screen of it.
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="mf-ask reveal" data-delay="1">
              <div className="mf-ask-q">
                <Search size={17} strokeWidth={2} />
                <span>when did Sara say she was moving?</span>
                <i className="mf-caret" aria-hidden="true" />
              </div>
              <div className="mf-ask-a">
                Sara is moving to <b>Lisbon in October</b>. You mentioned it on{' '}
                <b>14 August</b>, the day standup went badly.
                <span className="mf-ask-src">
                  <Bookmark size={13} strokeWidth={2} />
                  Rough standup, better afternoon · 14 Aug
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        title={
          <>
            Want MindFlow — or this on <span className="si">your own data</span>?
          </>
        }
        lede="Ask about early access, or about the same extraction pipeline running over your business's writing. Tell us which and we'll show you."
        primaryLabel="Talk to us about MindFlow"
      />
    </>
  );
}
